import {
  ID_SEQUENCE_PATTERN,
  stripHandlePrefixes,
  TWEET_SCHEME_PATTERN,
  URL_PREFIX_PATTERN,
  USERNAME_PATTERN,
} from '@/lib/utils/regex';

const TWITTER_HOSTS = new Set([
  'twitter.com',
  'www.twitter.com',
  'mobile.twitter.com',
  'm.twitter.com',
  'x.com',
  'www.x.com',
]);

const RESERVED_PROFILE_SEGMENTS = new Set([
  'about',
  'account',
  'compose',
  'explore',
  'home',
  'i',
  'intent',
  'login',
  'logout',
  'messages',
  'notifications',
  'privacy',
  'search',
  'settings',
  'share',
  'signup',
  'status',
  'statuses',
  'terms',
]);

function parseUrl(rawUrl: string): URL | null {
  if (typeof rawUrl !== 'string' || rawUrl.trim().length === 0) {
    return null;
  }

  const trimmed = rawUrl.trim();
  const maybePrefixed = URL_PREFIX_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return new URL(maybePrefixed);
  } catch {
    return null;
  }
}

function isKnownTwitterHost(hostname: string): boolean {
  return TWITTER_HOSTS.has(hostname.toLowerCase());
}

function normalisePathSegments(url: URL): string[] {
  return url.pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

/**
 * Extracts a tweet ID from a Twitter or X URL.
 * Supports canonical user URLs (x.com/username/status/123),
 * web intents (x.com/i/web/status/123), and internal `tweet:ID` links.
 */
export function extractTweetIdFromUrl(rawUrl: string): string | null {
  const directMatch = typeof rawUrl === 'string' ? rawUrl.trim().match(TWEET_SCHEME_PATTERN) : null;
  if (directMatch?.[1]) {
    return directMatch[1];
  }

  const parsed = parseUrl(rawUrl);
  if (!(parsed && isKnownTwitterHost(parsed.hostname))) {
    return null;
  }

  const segments = normalisePathSegments(parsed);
  if (segments.length === 0) {
    return null;
  }

  const statusIndex = segments.findIndex((segment) => {
    const normalized = segment.toLowerCase();
    return normalized === 'status' || normalized === 'statuses';
  });

  if (statusIndex === -1 || statusIndex >= segments.length - 1) {
    return null;
  }

  const candidate = segments[statusIndex + 1];
  if (!candidate) {
    return null;
  }

  const idMatch = ID_SEQUENCE_PATTERN.exec(candidate);
  return idMatch ? idMatch[0] : null;
}

/**
 * Extracts a username from a Twitter or X profile URL.
 * Only returns a username when the URL points directly to a profile without
 * extra path segments (e.g. `https://x.com/vercel`).
 */
export function extractUsernameFromUrl(rawUrl: string): string | null {
  const parsed = parseUrl(rawUrl);
  if (!(parsed && isKnownTwitterHost(parsed.hostname))) {
    return null;
  }

  const segments = normalisePathSegments(parsed);
  if (segments.length !== 1) {
    return null;
  }

  const segment = segments[0];
  if (!segment) {
    return null;
  }

  const normalized = segment.toLowerCase();
  if (RESERVED_PROFILE_SEGMENTS.has(normalized)) {
    return null;
  }

  const username = stripHandlePrefixes(segment);
  if (!USERNAME_PATTERN.test(username)) {
    return null;
  }

  return username;
}

export function buildTweetUrl(tweetId: string, username?: string | null): string {
  if (username && username.length > 0) {
    return `https://x.com/${username}/status/${tweetId}`;
  }
  return `https://x.com/i/web/status/${tweetId}`;
}

export function buildUserUrl(username: string): string {
  return `https://x.com/${username}`;
}
